import { useEffect, useState } from 'react';
import { tr } from '../i18n/tr';
import { GoogleCancelled, preloadFirebase } from '../lib/firebase';
import { useSession } from '../lib/session';
import './google-auth.css';

/** Google'ın çok renkli "G" logosu (marka kuralı: renkleri değiştirilmez). */
export function GoogleMark({ size = 22 }: { size?: number }) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden="true" className="google-mark">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

/** Beyaz "Google ile …" butonu. Hata olursa alttaki bildirim şeridinde gösterilir. */
export function GoogleButton({ label, onDone }: { label: string; onDone?(): void }) {
  const { signInWithGoogle, notify } = useSession();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void preloadFirebase().catch(() => {});
  }, []);

  async function click() {
    setBusy(true);
    try {
      await signInWithGoogle();
      onDone?.();
    } catch (err) {
      if (!(err instanceof GoogleCancelled)) notify(err instanceof Error ? err.message : tr.account.failed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button type="button" className="btn btn-block google-btn" onClick={() => void click()} disabled={busy}>
      <GoogleMark />
      {busy ? tr.account.signingIn : label}
    </button>
  );
}

/** Profil sayfasındaki hesap kartı: bağlı değilse bağla, bağlıysa e-posta ve çıkış. */
export function AccountCard() {
  const { account, signOut, notify } = useSession();
  if (account) {
    return (
      <section className="account-card account-card-linked" aria-label={tr.account.linked}>
        <span className="account-mark">
          <GoogleMark size={24} />
        </span>
        <span className="account-text">
          <span className="account-title display">{tr.account.linked}</span>
          {account.email && <span className="account-email">{account.email}</span>}
        </span>
        <button type="button" className="btn btn-ghost" onClick={() => void signOut()}>
          {tr.account.signOut}
        </button>
      </section>
    );
  }
  return (
    <section className="account-card" aria-label={tr.account.linkTitle}>
      <span className="sticker account-sticker">{tr.account.sticker}</span>
      <h2 className="display account-title">{tr.account.linkTitle}</h2>
      <p className="account-lead">{tr.account.linkLead}</p>
      <GoogleButton label={tr.account.link} onDone={() => notify(tr.account.linkedToast)} />
    </section>
  );
}

/** Giriş ekranı: "ya da" ayırıcısı + Google ile devam. */
export function WelcomeGoogle() {
  return (
    <div className="welcome-google">
      <span className="welcome-or" aria-hidden="true">
        {tr.account.or}
      </span>
      <GoogleButton label={tr.account.continue} />
      <p className="welcome-google-hint">{tr.account.continueHint}</p>
    </div>
  );
}
